import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { OpCode, Select } from '@aline/common';
import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import clsx from 'clsx';
import type { PanelVariableType } from 'src/components';
import {
  AlineRadio,
  AlineTooltip,
  RadioLabelWithCaption,
  useOnChangeFocusVariable,
  useUpdateOptionsVariableValue,
  useVariableStyles,
  VariableRowHeader,
} from 'src/components';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { domElIdCreators } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  option: {
    marginRight: theme.spacing(1),
  },
  radioGroup: {
    flexGrow: 1,
    outline: 'none',
    padding: theme.spacing(0, 1),
  },
}));

interface OptionsVariableProps extends React.HTMLAttributes<HTMLElement> {
  documentId: UUID;
  isSelected: boolean;
  optsVariable: PanelVariableType;
}

export function RadioOptionsVariable({
  className,
  documentId,
  isSelected,
  optsVariable,
}: OptionsVariableProps): JSX.Element {
  const { classes } = useStyles();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const { disabledReason } = useGetFieldDisabledReason(optsVariable);
  const {
    onClickVariable,
    options,
    resetOnBlur,
    selectedAnswer,
    setSelectedAnswer,
    updateOptionsVariable,
  } = useUpdateOptionsVariableValue(optsVariable, { isFocused });
  const isCompleted =
    typeof optsVariable.value === 'string' || Boolean(optsVariable.selectedAnswer);
  const { classes: variableClasses } = useVariableStyles({ isCompleted, isSelected });

  // No input element to focus...
  const { onFocus } = useOnChangeFocusVariable(optsVariable, { inputEl: null, liEl });

  return (
    <li
      ref={setLiEl}
      aria-selected={isSelected}
      className={clsx(className, variableClasses.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      id={domElIdCreators.variableRowEl(optsVariable.nodeId)}
      onClick={onClickVariable}
      onFocus={onFocus}
    >
      <VariableRowHeader
        documentId={documentId}
        isCompleted={isCompleted}
        variable={optsVariable}
      />
      {optsVariable.question && (
        <Typography className={variableClasses.question}>{optsVariable.question}</Typography>
      )}
      <RadioGroup
        className={classes.radioGroup}
        value={selectedAnswer?.id ?? null}
        onChange={async e => {
          const selectedOption = optsVariable.answerOptions.options.find(
            o => o.id === e.target.value
          );
          await updateOptionsVariable(selectedOption ?? null);
        }}
      >
        {options.map(option => {
          const isDefaultOption = option.id === optsVariable.defaultAnswer?.id;

          return (
            <AlineTooltip key={option.id} arrow placement="top" title={disabledReason}>
              <div>
                <FormControlLabel
                  className={classes.option}
                  control={
                    <AlineRadio
                      checked={option.id === selectedAnswer?.id}
                      disabled={Boolean(disabledReason)}
                      size="small"
                    />
                  }
                  data-cy={Select.document.editor.rightDrawer.outline.radioOptionsButton}
                  disabled={Boolean(disabledReason)}
                  label={
                    <RadioLabelWithCaption
                      caption={isDefaultOption ? '(Default)' : undefined}
                      label={option.optionText}
                    />
                  }
                  value={option.id}
                  onBlur={() => {
                    const opCode = resetOnBlur();
                    if (opCode === OpCode.Success) setSelectedAnswer(null);

                    setIsFocused(false);
                  }}
                  onFocus={() => setIsFocused(true)}
                />
              </div>
            </AlineTooltip>
          );
        })}
      </RadioGroup>
    </li>
  );
}
